import { InjectionToken } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BotInfo, BrowserInfo, NodeInfo, ReactNativeInfo, SearchBotDeviceInfo, detect } from 'detect-browser';
import * as appActions from 'src/app/store/actions/app.actions';
import { v4 as uuidv4 } from 'uuid';
import { DialogBodyComponent } from '../components/dialogs/dialog-body.component';
import { CanvasData } from '../components/whiteboard/whiteboard.component';
import { AppModesEnum } from '../models/WebServer/appModesEnum';
import { DynamicCreateEvent } from '../models/WebServer/dynamicCreateEvent';
import { DynamicEventData } from '../models/WebServer/dynamicEventData';
import { PartecipantRoles } from '../models/WebServer/partecipantRoles';
import { PartecipantStatus } from '../models/WebServer/partecipantStatus';
import { SessionConfig } from '../models/WebServer/sessionConfig';
import { DialogContent } from '../models/dialogContent';
import { PhoneStatusEnum } from '../models/phoneStatus';
import { AppConfigService } from '../services/app.config.service';
import { FELoggerService } from '../services/feLogger.service';
import { I18Service } from '../services/i18.service';
import { AppState } from '../store/states/app.state';
import { BrowserHelper } from './browser.helper';


export const SAMEORIGIN = new InjectionToken<RegExp>("sameorigin.regex", { factory: () => {

  // Test the given URL to start with "data:" or "blob:" or the current host
  return new RegExp(`^data:|^blob:|^http(?:s)?:\/\/${window.location.host}`);

}});

export enum PageStatusEnum {
  active = "active",
  passive = "passive",
  hidden = "hidden",
  frozen = "frozen",
  terminated = "terminated"
}

export class AppHelper {
  static getTitleName():string { return AppConfigService.brandName; }
  static getVersion(): string { return document.head.getAttribute('client-version') || '0.0'; }
  static getBuild(): string { return document.head.getAttribute('client-build') || '0'; }
  static getRelease(): string { return `${this.getVersion()}.${this.getBuild()}`; }
  static getTitleVersion(): string { return `${this.getTitleName()} v${this.getVersion()}`;}
  static getTitleRelease(): string { return `${this.getTitleName()} v${this.getRelease()}`;}

  static browserDetection(): BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | ReactNativeInfo | undefined {
    const browser = detect();
    if (browser) {
      return browser;
    }
    return undefined;
  }

  static getEmptyDynamicCreateData(partecipantId: string, businessEventCode: string): DynamicCreateEvent {
    const dynEvtData: DynamicEventData = { eventInfo: null, partecipantId };
    const dynCreateEvt: DynamicCreateEvent = { data: dynEvtData, timestamp: new Date(), businessEventCode }
    return dynCreateEvt;
  }

  static getDynamicCreateData(partecipantId: string, businessEventCode: string, eventInfo: any = undefined): DynamicCreateEvent {
    var emptyData = this.getEmptyDynamicCreateData(partecipantId || 'none', businessEventCode);
    emptyData.data.eventInfo = eventInfo;
    return emptyData;
  }

  static getPageState(): PageStatusEnum {
    if (document.visibilityState === 'hidden') {
      return PageStatusEnum.hidden;
    }
    if (document.hasFocus()) {
      return PageStatusEnum.active;
    }
    return PageStatusEnum.passive;
  }


  private static filePickerOpened: boolean = false;
  static openFilePicker(store: Store<AppState>, divId: string) { 
    store.dispatch(appActions.setSuspended({ message: I18Service.get("Loading.FileSelection") }));
    document.getElementById(divId)?.click();
    AppHelper.filePickerOpened = true;
  }
  static focused(store: Store<AppState>) {
    if (AppHelper.filePickerOpened) {
      AppHelper.filePickerOpened = false;
      store.dispatch(appActions.setSuspended({ message: '' })); //exit suspended fileselection al focus chiusura picker
    }
  }

  // static openTab(url: any) {
  //   // Create link in memory
  //   console.log('URL ALLEGATO', url);
  //   var a = window.document.createElement("a");
  //   a.target = '_blank';
  //   a.href = url;

  //   // Dispatch fake click
  //   var e = window.document.createEvent("MouseEvents");
  //   e.initMouseEvent("click", true, true, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null);
  //   a.dispatchEvent(e);
  // }

  static openDialog(matDialog: MatDialog, dialogContent: DialogContent, width: string = '60%', height: string | undefined = undefined): MatDialogRef<DialogBodyComponent, any> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = dialogContent;
    dialogConfig.disableClose = dialogContent.disableClose;
    dialogConfig.width = width;
    if (height) {
      dialogConfig.height = height;
    }
    let dialogRef = matDialog.open(DialogBodyComponent, dialogConfig);
    return dialogRef;
  }

  static deepCopy(obj: any): any {
    var copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || "object" != typeof obj) return obj;

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (var i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      const copy: any = {};
      for (var attr in obj) {
        if (obj.hasOwnProperty(attr)) copy[attr] = this.deepCopy(obj[attr]);
      }
      return copy;
    }
    throw new Error("Unable to copy obj! Its type isn't supported.");
  }

  /**
   * Deep merge di due oggetti: i valori di source sovrascrivono quelli di target,
   * ma per gli oggetti annidati viene fatto un merge ricorsivo.
   * Gli array vengono sovrascritti (non concatenati).
   *
   * @param target Oggetto base con i valori di default
   * @param source Oggetto con i valori da applicare (può essere parziale)
   * @returns Nuovo oggetto risultato del merge
   */
  static deepMerge<T>(target: T, source: Partial<T> | undefined | null): T {
    // Se source è null/undefined, ritorna una copia di target
    if (source === null || source === undefined) {
      return this.deepCopy(target);
    }

    // Se target è null/undefined, ritorna una copia di source
    if (target === null || target === undefined) {
      return this.deepCopy(source) as T;
    }

    // Se uno dei due non è un oggetto "plain", source sovrascrive
    if (!this.isPlainObject(target) || !this.isPlainObject(source)) {
      return this.deepCopy(source) as T;
    }

    // Merge ricorsivo degli oggetti
    const result: any = { ...target };

    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        const sourceValue = (source as any)[key];
        const targetValue = (target as any)[key];

        if (this.isPlainObject(sourceValue) && this.isPlainObject(targetValue)) {
          // Entrambi sono oggetti plain: merge ricorsivo
          result[key] = this.deepMerge(targetValue, sourceValue);
        } else if (sourceValue !== undefined) {
          // Altrimenti source sovrascrive (inclusi array, Date, null, primitivi)
          result[key] = this.deepCopy(sourceValue);
        }
      }
    }

    return result;
  }

  /**
   * Controlla se un valore è un oggetto "plain" (non Array, Date, null, ecc.)
   */
  private static isPlainObject(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value !== 'object') return false;
    if (Array.isArray(value)) return false;
    if (value instanceof Date) return false;
    // Controlla che il prototype sia Object.prototype
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
  }


  // static openSnackBar(snackBar: MatSnackBar, message: string, action: string, snackDuration: number = 2000) {
  //   action = '';
  //   snackBar.open(message, action, {
  //     duration: snackDuration,
  //     verticalPosition: 'top',
  //     horizontalPosition: 'center'
  //   });
  // }

  static imageExists(url: string, callback: Function) {
    const img = new Image();
    img.onload = () => { callback(true); };
    img.onerror = () => { callback(false); };
    img.src = url;
  }

  static fileExists(fileUrl: string): boolean {
    console.log(`fileExists ${fileUrl}`);
    var http = new XMLHttpRequest();
    try {
      http.open('HEAD', fileUrl, false);
      http.send();
    } catch (e) { }
    return http.status >= 200 && http.status <= 299;
  }

  static loadTextFile(fileUrl: string): string {
    var http = new XMLHttpRequest();
    try {
      http.open('GET', fileUrl, false);
      http.send();
    } catch (e) { }
    return http.responseText;
  }



  static getInitialAppMode(config: SessionConfig, role: PartecipantRoles, anyOperatorOnline: boolean): AppModesEnum[] {
    switch (role) {
      case PartecipantRoles.SecondaryClient:
        // if(!anyOperatorOnline) {
        //   console.log(`[APPMODES] getInitialAppMode → SecondaryClient, no operators online → []`);
        //   return [];
        // }
        const modes = this.getModeWhenPrimaryArrives(config, role);
        console.log(`[APPMODES] getInitialAppMode → SecondaryClient, operators online, localization: ${config.localization} → [${modes}]`);
        return modes;

      case PartecipantRoles.PrimaryClient:
      case PartecipantRoles.SuperClient:
      case PartecipantRoles.GuestClient:
      case PartecipantRoles.OnSiteOperator:

        const modesOp = this.getModeWhenPrimaryArrives(config, role);
        if (modesOp.length > 0) {
          return modesOp;
        }

        if (config.chat) return [AppModesEnum.Chat];
        if (config.video) return [AppModesEnum.Video];
        if (config.customerAttachments) return [AppModesEnum.Photo];

        break;
    }
    return [];
  }

  static getModeWhenPrimaryArrives(config: SessionConfig, role: PartecipantRoles): AppModesEnum[] {
    switch (role) {
      case PartecipantRoles.SecondaryClient:
        if(config.agencySettings.initialSecondaryAppMode) {
          return config.agencySettings.initialSecondaryAppMode;
        }
        if (config.localization) return [AppModesEnum.Map]; // Esplicita Map invece di []
        if (config.chat) return [AppModesEnum.Chat];
        if (config.video) return [AppModesEnum.Video];
        if (config.customerAttachments) return [AppModesEnum.Photo];


        return []; //non visualizza nulla!!!

      case PartecipantRoles.OnSiteOperator:
        if(config.agencySettings.initialOnsiteOperatorAppMode) {
          return config.agencySettings.initialOnsiteOperatorAppMode;
        }
        return [];

      // case PartecipantRoles.PrimaryClient:
      //   if (config.chat) return AppModesEnum.Chat;
      //   if (config.customerAttachments) return AppModesEnum.Photo;
      //   if (config.audioVideo) return AppModesEnum.Video

      //   break;
      // case PartecipantRoles.GuestClient:
      //   if (config.chat) return AppModesEnum.Chat;
      //   if (config.audioVideo) return AppModesEnum.Video
      //   break;
    }
    return [];
  }

  static getHome(sessionConfig: SessionConfig, role: PartecipantRoles, anyOpOnline: boolean) {
    
    if(role === PartecipantRoles.SecondaryClient) { // qualora sia impostata la "pagina di atterraggio" la home per il secondario è diversa da initialAppMode
      const modes = 
        sessionConfig.localization ? [AppModesEnum.Map] :
        sessionConfig.chat ? [AppModesEnum.Chat] :
        AppHelper.getInitialAppMode(
          sessionConfig,
          role,
          anyOpOnline
        );
        return modes;
      }

      // per gli altri la "home" equivale alla pagina di atterraggio #95330
      return AppHelper.getInitialAppMode(
        sessionConfig,
        role,
        anyOpOnline
      );

  }


  static getStatusColor(p: PartecipantStatus | undefined, operatorReady: boolean = false): {color: string, tooltip: string } {
    
    if (!p || !p.isRegistered) return {color: 'var(--status-red)', tooltip: I18Service.get('LABELS.NOTCONNECTEDSIP')};

    // if (p.videoEnabled || p.microphoneEnabled) return {color: 'var(--status-azure)', tooltip: I18Service.get('LABELS.READYTOSTREAM')};
    if (p.phoneStatus == PhoneStatusEnum.TALKING) return {color: 'var(--status-azure)', tooltip: I18Service.get('LABELS.READYTOSTREAM')};

    if (p.pageStatus !== PageStatusEnum.active) {
      return {color: 'var(--status-yellow)', tooltip: I18Service.get('LABELS.PASSIVE')};
    }


    // if (operatorReady){
    //   return {color: 'var(--status-green)', tooltip: I18Service.get('LABELS.USERPRESENCE')};
    // }
    // return {color: 'var(--status-yellow)', tooltip: I18Service.get('LABELS.CONNECTEDSIP')};

    // uniformare pallino verde quando online #44792
    return {color: 'var(--status-green)', tooltip: I18Service.get('LABELS.CONNECTEDSIP')};
  }


   static getCanvasFromVideo(video: HTMLVideoElement): HTMLCanvasElement | undefined {
        // video.pause();
        // from: https://github.com/kasprownik/electron-screencapture/blob/master/index.js
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth; //video.clientWidth || video.videoWidth;
        canvas.height = video.videoHeight; //video.clientHeight || video.videoHeight;
        const context = canvas.getContext('2d');

        if (!context) {
          FELoggerService.error(`getCanvasFromVideo context '2d' not found`)
          return;
        }
        // see: https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement
        context?.drawImage(video, 0, 0, canvas.width, canvas.height);
        console.log(`****** editVideo w:${canvas.width} h:${canvas.height}`);
        return canvas;
   }
   
   static getCanvasFromImage(img: HTMLImageElement | undefined): HTMLCanvasElement | undefined {
    if(!img) return;
    img.crossOrigin="anonymous";
    // from: https://github.com/kasprownik/electron-screencapture/blob/master/index.js
    const canvas = document.createElement('canvas');

    // Usa le dimensioni native dell'immagine (come per il video)
    // Il ridimensionamento sarà gestito successivamente da resizeCanvasIfNeeded
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const context = canvas.getContext('2d');

    if (!context) {
      FELoggerService.error(`getCanvasFromImage context '2d' not found`)
      return;
    }
    // see: https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement
    context?.drawImage(img, 0, 0, canvas.width, canvas.height);
    console.log(`****** canvasFromImage w:${canvas.width} h:${canvas.height}`);
    return canvas;
  }

  static async getBase64ImageFromUrl(url: string, width:number, height:number):Promise<CanvasData> {

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Necessario per evitare problemi di CORS
  
      img.onload = () => {
        // Creazione del canvas
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
  
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Impossibile ottenere il contesto del canvas');
          return;
        }
  
        // Disegna l'immagine sul canvas con le dimensioni specificate
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  
        // Converte il canvas in una stringa base64
        const base64Image = canvas.toDataURL('image/png'); // Cambia 'image/png' in 'image/jpeg' se necessario
        resolve({id: AppHelper.newUUID, height: canvas.height,image: base64Image, width: canvas.width});
      };
  
      img.onerror = () => reject('Errore durante il caricamento dell\'immagine');
      img.src = url; // Carica l'immagine dall'URL
    });
  }

  static clickOnBody(){
    document.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  }

  static resizeRectangleMaintainingAspectRatio(srcWidth: number, srcHeight: number, maxWidth: number, maxHeight: number) {
    const ratio = Math.min((maxWidth ) / srcWidth, (maxHeight) / srcHeight);

    return {
        width: Math.floor(srcWidth * ratio),
        height: Math.floor(srcHeight * ratio),
        ratio
    };
  }

  /**
   * Ridimensiona un canvas se supera le dimensioni massime specificate
   * @param sourceCanvas Canvas originale da ridimensionare
   * @param maxWidth Larghezza massima
   * @param maxHeight Altezza massima
   * @returns Canvas ridimensionato (o l'originale se non serve ridimensionare)
   */
  static resizeCanvasIfNeeded(sourceCanvas: HTMLCanvasElement, maxWidth: number, maxHeight: number): HTMLCanvasElement {
    // Se il canvas è già entro i limiti, ritornalo senza modifiche
    if (sourceCanvas.width <= maxWidth && sourceCanvas.height <= maxHeight) {
      console.log(`Canvas ${sourceCanvas.width}x${sourceCanvas.height} already within limits ${maxWidth}x${maxHeight}, no resize needed`);
      return sourceCanvas;
    }

    // Calcola le nuove dimensioni mantenendo aspect ratio
    const resized = this.resizeRectangleMaintainingAspectRatio(
      sourceCanvas.width,
      sourceCanvas.height,
      maxWidth,
      maxHeight
    );

    console.log(`Resizing canvas from ${sourceCanvas.width}x${sourceCanvas.height} to ${resized.width}x${resized.height} (ratio: ${resized.ratio.toFixed(3)})`);

    // Crea un nuovo canvas con le dimensioni ridotte
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = resized.width;
    resizedCanvas.height = resized.height;

    const context = resizedCanvas.getContext('2d');
    if (!context) {
      FELoggerService.error(`resizeCanvasIfNeeded: context '2d' not found`);
      return sourceCanvas; // Ritorna l'originale in caso di errore
    }

    // Ridisegna l'immagine nel canvas ridimensionato
    context.drawImage(sourceCanvas, 0, 0, resized.width, resized.height);

    return resizedCanvas;
  }

  static  get newUUID(): string { return uuidv4();  }

  /**
   * Scarica un file in modo compatibile con tutti i browser, incluso iOS Safari.
   * Su iOS Safari, window.open() e target="_blank" possono causare un reload della SPA,
   * quindi usiamo un approccio basato su fetch blob + link download.
   *
   * @param url URL del file da scaricare
   * @param fileName Nome del file per il download (opzionale)
   */
  static downloadFile(url: string, fileName?: string): void {
    const isApple = BrowserHelper.isAppleOS();

    console.log(`downloadFile: url=${url}, fileName=${fileName}, isApple=${isApple}`);

    // Su iOS Safari, l'attributo download non funziona sempre, ma almeno
    // non causa un reload della pagina come window.open()
    // Usiamo fetch per ottenere il blob e poi creiamo un Object URL
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Crea un Object URL dal blob
        const blobUrl = URL.createObjectURL(blob);

        // Crea un link temporaneo
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName || AppHelper.extractFileNameFromUrl(url) || 'download';
        // NON impostare target="_blank" - causa problemi su iOS
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Revoca l'Object URL dopo un breve delay per permettere il download
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      })
      .catch(error => {
        console.error('downloadFile: fetch failed, fallback to direct link', error);
        // Fallback: prova con un link diretto (potrebbe non funzionare su iOS)
        AppHelper.downloadFileFallback(url, fileName);
      });
  }

  /**
   * Fallback per il download quando fetch fallisce (es. CORS)
   */
  private static downloadFileFallback(url: string, fileName?: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || AppHelper.extractFileNameFromUrl(url) || 'download';
    // NON impostare target="_blank"
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Estrae il nome del file da un URL
   */
  private static extractFileNameFromUrl(url: string): string | undefined {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      // Rimuovi eventuali query string
      return lastPart ? decodeURIComponent(lastPart.split('?')[0]) : undefined;
    } catch {
      // Se l'URL non è valido, prova un parsing semplice
      const parts = url.split('/');
      const lastPart = parts[parts.length - 1];
      return lastPart ? lastPart.split('?')[0] : undefined;
    }
  }
  }




/**
 * Funzione di diagnostica completa per webapp Angular in WebView Android
 * Da chiamare dopo che l'app è completamente caricata (es. in ngAfterViewInit)
 */
export interface WebViewDiagnostics {
  timestamp: string;
  session: {
    url: string;
    referrer: string;
    loadTime: number;
  };
  environment: {
    userAgent: string;
    isLikelyWebView: boolean;
    isStandaloneApp: boolean;
    hasAndroidInterface: boolean;
    hasChrome: boolean;
  };
  screen: {
    physical: {
      width: number;
      height: number;
      availWidth: number;
      availHeight: number;
      pixelRatio: number;
    };
    orientation: {
      type: string;
      angle: number;
    };
  };
  window: {
    inner: { width: number; height: number };
    outer: { width: number; height: number };
    scroll: { x: number; y: number };
  };
  viewport: {
    metaTag: string;
    effectiveZoom: number;
  };
  dom: {
    html: {
      client: { width: number; height: number };
      scroll: { width: number; height: number };
      offset: { width: number; height: number };
    };
    body: {
      client: { width: number; height: number };
      scroll: { width: number; height: number };
      offset: { width: number; height: number };
      computed: {
        width: string;
        height: string;
        maxWidth: string;
        maxHeight: string;
        overflow: string;
        position: string;
      };
    };
  };
  viewportUnits: {
    vh100InPixels: number;
    vw100InPixels: number;
    vhMatchesWindow: boolean;
    vwMatchesWindow: boolean;
    vhVsInnerHeight: number;
    vwVsInnerWidth: number;
  };
  issues: {
    hasScrollbars: { horizontal: boolean; vertical: boolean };
    dimensionMismatches: string[];
    potentialProblems: string[];
  };
}

export function collectWebViewDiagnostics(): WebViewDiagnostics {
  const startTime = performance.now();
  
  // Elementi DOM principali
  const html = document.documentElement;
  const body = document.body;
  
  // Test viewport units
  function testViewportUnits() {
    const testVh = document.createElement('div');
    const testVw = document.createElement('div');
    
    testVh.style.cssText = 'position:absolute;top:-9999px;height:min(100dvh,100vh);width:1px;visibility:hidden;';
    testVw.style.cssText = 'position:absolute;top:-9999px;height:1px;width:min(100dvw,100vw);visibility:hidden;';
    
    document.body.appendChild(testVh);
    document.body.appendChild(testVw);
    
    const vhPixels = testVh.offsetHeight;
    const vwPixels = testVw.offsetWidth;
    
    document.body.removeChild(testVh);
    document.body.removeChild(testVw);
    
    return {
      vh100InPixels: vhPixels,
      vw100InPixels: vwPixels,
      vhMatchesWindow: Math.abs(vhPixels - window.innerHeight) < 2,
      vwMatchesWindow: Math.abs(vwPixels - window.innerWidth) < 2,
      vhVsInnerHeight: vhPixels - window.innerHeight,
      vwVsInnerWidth: vwPixels - window.innerWidth
    };
  }
  
  // Detecta ambiente
  function detectEnvironment() {
    const ua = navigator.userAgent;
    const hasChrome = !!(window as any).chrome;
    const isWebView = !hasChrome && /Version.*Chrome/.test(ua);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const hasAndroidInterface = typeof (window as any).Android !== 'undefined' || 
                               typeof (window as any).AndroidInterface !== 'undefined';
    
    return {
      userAgent: ua,
      isLikelyWebView: isWebView,
      isStandaloneApp: isStandalone,
      hasAndroidInterface: hasAndroidInterface,
      hasChrome: hasChrome
    };
  }
  
  // Analizza problemi potenziali
  function analyzePotentialIssues(data: any) {
    const issues: string[] = [];
    const mismatches: string[] = [];
    
    // Check viewport units vs window
    if (Math.abs(data.viewportUnits.vhVsInnerHeight) > 50) {
      issues.push(`vh mismatch: ${data.viewportUnits.vhVsInnerHeight}px difference`);
    }
    
    if (Math.abs(data.viewportUnits.vwVsInnerWidth) > 50) {
      issues.push(`vw mismatch: ${data.viewportUnits.vwVsInnerWidth}px difference`);
    }
    
    // Check body vs window dimensions
    if (Math.abs(data.dom.body.client.height - data.window.inner.height) > 10) {
      mismatches.push(`body height (${data.dom.body.client.height}) vs window (${data.window.inner.height})`);
    }
    
    if (Math.abs(data.dom.body.client.width - data.window.inner.width) > 10) {
      mismatches.push(`body width (${data.dom.body.client.width}) vs window (${data.window.inner.width})`);
    }
    
    // Check screen vs window (possibili barre/interfacce)
    const heightLoss = data.screen.physical.height - data.window.inner.height;
    if (heightLoss > 200) {
      issues.push(`significant height loss: ${heightLoss}px (possible bars/interfaces)`);
    }
    
    // Check for viewport meta with height
    if (data.viewport.metaTag.includes('height=')) {
      issues.push('viewport meta includes height directive');
    }
    
    // Check per zoom
    if (Math.abs(data.viewport.effectiveZoom - 1) > 0.1) {
      issues.push(`zoom detected: ${data.viewport.effectiveZoom.toFixed(2)}`);
    }
    
    return { mismatches, potentialProblems: issues };
  }
  
  // Raccolta dati principale
  const environment = detectEnvironment();
  const viewportUnits = testViewportUnits();
  
  // Orientamento
  const orientation = {
    type: (screen as any).orientation?.type || 
          (window.innerHeight > window.innerWidth ? 'portrait-primary' : 'landscape-primary'),
    angle: (screen as any).orientation?.angle || (window as any).orientation || 0
  };
  
  // Meta viewport
  const viewportMeta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement;
  const effectiveZoom = Math.round((screen.width / window.innerWidth) * 100) / 100;
  
  // Computed styles del body
  const bodyStyles = window.getComputedStyle(body);
  
  const diagnostics: WebViewDiagnostics = {
    timestamp: new Date().toISOString(),
    
    session: {
      url: window.location.href,
      referrer: document.referrer,
      loadTime: Math.round(startTime)
    },
    
    environment,
    
    screen: {
      physical: {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        pixelRatio: window.devicePixelRatio || 1
      },
      orientation
    },
    
    window: {
      inner: { 
        width: window.innerWidth, 
        height: window.innerHeight 
      },
      outer: { 
        width: window.outerWidth, 
        height: window.outerHeight 
      },
      scroll: { 
        x: window.scrollX || window.pageXOffset, 
        y: window.scrollY || window.pageYOffset 
      }
    },
    
    viewport: {
      metaTag: viewportMeta?.content || 'not found',
      effectiveZoom
    },
    
    dom: {
      html: {
        client: { width: html.clientWidth, height: html.clientHeight },
        scroll: { width: html.scrollWidth, height: html.scrollHeight },
        offset: { width: html.offsetWidth, height: html.offsetHeight }
      },
      body: {
        client: { width: body.clientWidth, height: body.clientHeight },
        scroll: { width: body.scrollWidth, height: body.scrollHeight },
        offset: { width: body.offsetWidth, height: body.offsetHeight },
        computed: {
          width: bodyStyles.width,
          height: bodyStyles.height,
          maxWidth: bodyStyles.maxWidth,
          maxHeight: bodyStyles.maxHeight,
          overflow: bodyStyles.overflow,
          position: bodyStyles.position
        }
      }
    },
    
    viewportUnits,
    
    issues: {
      hasScrollbars: {
        horizontal: body.scrollWidth > body.clientWidth,
        vertical: body.scrollHeight > body.clientHeight
      },
      dimensionMismatches: [],
      potentialProblems: []
    }
  };
  
  // Analizza i problemi
  const issuesAnalysis = analyzePotentialIssues(diagnostics);
  diagnostics.issues.dimensionMismatches = issuesAnalysis.mismatches;
  diagnostics.issues.potentialProblems = issuesAnalysis.potentialProblems;
  
  return diagnostics;
}

/**
 * Versione semplificata per logging rapido
 */
export function collectQuickDiagnostics() {
  const data = collectWebViewDiagnostics();
  
  return {
    timestamp: data.timestamp,
    device: `${data.screen.physical.width}x${data.screen.physical.height} (${data.screen.physical.pixelRatio}x)`,
    window: `${data.window.inner.width}x${data.window.inner.height}`,
    body: `${data.dom.body.client.width}x${data.dom.body.client.height}`,
    viewport: data.viewport.metaTag,
    isWebView: data.environment.isLikelyWebView,
    issues: data.issues.potentialProblems.length,
    problems: data.issues.potentialProblems.slice(0, 3) // Prime 3 issues
  };
}

/**
 * Funzione per Angular - da chiamare in ngAfterViewInit
 */
export function initWebViewDiagnostics(logFunction?: (data: any) => void) {
  // Aspetta che la view sia completamente renderizzata
  setTimeout(() => {
    const diagnostics = collectWebViewDiagnostics();
    
    // Log in console per debug locale
    console.group('🔍 WebView Diagnostics');
    console.log('Full data:', diagnostics);
    console.log('Quick summary:', collectQuickDiagnostics());
    
    if (diagnostics.issues.potentialProblems.length > 0) {
      console.warn('⚠️ Potential issues found:', diagnostics.issues.potentialProblems);
    }
    console.groupEnd();
    
    // Invia al server se fornita la funzione di log
    if (logFunction) {
      logFunction({
        type: 'webview_diagnostics',
        data: diagnostics
      });
    }
  }, 100);
}

// Esempio di utilizzo in componente Angular:
/*
export class AppComponent implements AfterViewInit {
  
  ngAfterViewInit() {
    initWebViewDiagnostics((data) => {
      // Invia al tuo servizio di logging
      this.loggingService.logDiagnostics(data);
    });
  }
}
*/